import { Component, inject, signal, computed, HostListener } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../auth/auth.service';
import { FeatureFlagsService } from '../../feature-flags/feature-flags.service';
import { FeatureFlagCodes } from '../../feature-flags/feature-flag-codes';
import { APP_PATHS } from '../../routing/app-paths';

import type { FeatureFlagCode } from '../../feature-flags/feature-flag-codes';

interface NavItem {
  path: string;
  labelKey: string;
  icon: string;
  activeIcon: string;
  requiresAuth?: boolean;
  /** Shown only while this platform flag is active. */
  requiresFlag?: FeatureFlagCode;
}

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, TranslateModule],
  templateUrl: './app-shell.component.html',
  styleUrl: './app-shell.component.scss',
})
export class AppShellComponent {
  protected readonly auth  = inject(AuthService);
  // Kicks off the one-time platform flag fetch as soon as any product screen
  // renders; marketing pages never construct this shell.
  protected readonly flags = inject(FeatureFlagsService);
  protected sidebarExpanded = signal(false);

  constructor() {
    this.flags.load();
  }

  // Web product: browse → book → my bookings, events, settings. Teams and
  // favorites stay app-only for now — their routes are not mounted in
  // app.routes.ts, so they must not appear here either (see the note there).
  readonly navItems: NavItem[] = [
    { path: APP_PATHS.complexes, labelKey: 'nav.browse',       icon: 'search',   activeIcon: 'search' },
    { path: APP_PATHS.bookings,  labelKey: 'nav.reservations', icon: 'calendar', activeIcon: 'calendar-fill', requiresAuth: true },
    { path: APP_PATHS.events,    labelKey: 'nav.events',       icon: 'trophy',   activeIcon: 'trophy-fill', requiresFlag: FeatureFlagCodes.events },
    { path: APP_PATHS.profile,   labelKey: 'nav.settings',     icon: 'settings', activeIcon: 'settings' },
    // App-only until their web screens ship:
    // { path: APP_PATHS.teams,  labelKey: 'nav.teams',        icon: 'people', activeIcon: 'people-fill', requiresAuth: true },
  ];

  // Hide auth-only tabs for signed-out users so they don't tap into dead-ends,
  // and flag-gated tabs until the platform says the surface exists.
  readonly visibleNavItems = computed(() =>
    this.navItems.filter(item =>
      (this.auth.isAuthenticated() || !item.requiresAuth)
      && (!item.requiresFlag || this.flags.isEnabled(item.requiresFlag)),
    )
  );

  @HostListener('window:resize')
  onResize(): void {
    // collapse sidebar on narrow viewports
    if (window.innerWidth < 1024) {
      this.sidebarExpanded.set(false);
    }
  }

  toggleSidebar(): void {
    this.sidebarExpanded.update(v => !v);
  }
}
